import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, User } from 'lucide-react';
import api from '../services/api';

function EditarMulta() {
  const [multa, setMulta] = useState(null);
  const [formData, setFormData] = useState({
    conductor_dni: '',
    conductor_nombre: '',
    conductor_apellido1: '',
    conductor_apellido2: '',
    conductor_email: '',
    conductor_telefono: '',
    conductor_direccion: '',
    conductor_codigo_postal: '',
    conductor_poblacion: '',
    conductor_provincia: '',
    conductor_permiso_conducir: '',
    conductor_pais_permiso: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    cargarMulta();
  }, [id]);

  const cargarMulta = async () => {
    try {
      const response = await api.get('/admin/multas/pendientes');
      const multaEncontrada = response.data.data.find(m => m.id === parseInt(id));

      if (!multaEncontrada) {
        alert('Multa no encontrada');
        navigate('/admin/multas/pendientes');
        return;
      }

      setMulta(multaEncontrada);
    } catch (error) {
      console.error('Error cargando multa:', error);
      // El interceptor de api.js maneja el 401 automáticamente
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // BUG-018: pre-check cliente — detectar CIF en campo DNI antes de mandar al backend
    const dni = (formData.conductor_dni || '').trim().toUpperCase();
    const REGEX_CIF = /^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/;
    const REGEX_DNI_NIE = /^[XYZ]?\d{7,8}[A-Z]$/;
    if (dni && REGEX_CIF.test(dni)) {
      alert(
        `El valor "${dni}" parece un CIF de empresa, no un DNI/NIE.\n\n` +
        `Esta pantalla solo gestiona conductores persona física.\n\n` +
        `Para identificar una empresa cesionaria:\n` +
        `1) Vuelve al listado de multas\n` +
        `2) Abre el modal "Cambiar identificación" desde la columna de acciones\n` +
        `3) Selecciona en el desplegable "Empresa cesionaria (persona jurídica)"\n` +
        `4) Mete ahí el CIF + razón social + datos`
      );
      return;
    }
    if (dni && !REGEX_DNI_NIE.test(dni)) {
      alert(`El DNI/NIE "${dni}" no tiene formato válido. Formato esperado: 12345678A o X1234567L`);
      return;
    }

    setSaving(true);

    try {
      await api.put(`/admin/multas/${id}`, formData);

      alert('Multa actualizada correctamente');
      navigate('/admin/multas/pendientes');
    } catch (error) {
      console.error('Error actualizando multa:', error);
      if (error.response?.status === 401) {
        alert('Su sesión ha expirado. Por favor, inicie sesión de nuevo.');
        return;
      }
      // BUG-018: mostrar detalles específicos de validación si el backend los devuelve
      const data = error.response?.data;
      if (data?.details && Array.isArray(data.details) && data.details.length > 0) {
        const errores = data.details
          .map(d => `• ${d.field || 'campo'}: ${d.message}`)
          .join('\n');
        alert(`No se pudo guardar — errores de validación:\n\n${errores}`);
      } else {
        alert(data?.message || data?.error || 'Error al actualizar la multa');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/multas/pendientes')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Identificar Conductor</h1>
            <p className="text-sm text-gray-600">Multa {multa?.numero_expediente}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info de la multa */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Datos de la Multa</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Empresa</p>
              <p className="font-medium">{multa?.empresa_nombre}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Matrícula</p>
              <p className="font-medium">{multa?.matricula}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha Infracción</p>
              <p className="font-medium">
                {new Date(multa?.fecha_infraccion).toLocaleDateString('es-ES')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Organismo</p>
              <p className="font-medium">{multa?.organismo_emisor || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Formulario conductor */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-lg">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Datos del Conductor</h2>
          </div>

          <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <strong>Esta pantalla es solo para conductor persona física</strong> (DNI o NIE).
            Si quien debe responder por la multa es una empresa cesionaria, vuelve al listado de multas
            y usa el modal <em>"Cambiar identificación"</em> seleccionando <em>"Empresa cesionaria (persona jurídica)"</em>.
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DNI/NIE *
                </label>
                <input
                  type="text"
                  value={formData.conductor_dni}
                  onChange={(e) => setFormData({...formData, conductor_dni: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.conductor_nombre}
                  onChange={(e) => setFormData({...formData, conductor_nombre: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primer Apellido *
                </label>
                <input
                  type="text"
                  value={formData.conductor_apellido1}
                  onChange={(e) => setFormData({...formData, conductor_apellido1: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Segundo Apellido
                </label>
                <input
                  type="text"
                  value={formData.conductor_apellido2}
                  onChange={(e) => setFormData({...formData, conductor_apellido2: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.conductor_email}
                  onChange={(e) => setFormData({...formData, conductor_email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.conductor_telefono}
                  onChange={(e) => setFormData({...formData, conductor_telefono: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección (calle, número, piso) *
              </label>
              <input
                type="text"
                value={formData.conductor_direccion}
                onChange={(e) => setFormData({...formData, conductor_direccion: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: C/ Gran Vía 28, 3º B"
                required
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código Postal *
                </label>
                <input
                  type="text"
                  value={formData.conductor_codigo_postal}
                  onChange={(e) => setFormData({...formData, conductor_codigo_postal: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="28001"
                  maxLength="5"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Población *
                </label>
                <input
                  type="text"
                  value={formData.conductor_poblacion}
                  onChange={(e) => setFormData({...formData, conductor_poblacion: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Madrid"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provincia *
                </label>
                <input
                  type="text"
                  value={formData.conductor_provincia}
                  onChange={(e) => setFormData({...formData, conductor_provincia: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Madrid"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nº Permiso de Conducir
                </label>
                <input
                  type="text"
                  value={formData.conductor_permiso_conducir}
                  onChange={(e) => setFormData({...formData, conductor_permiso_conducir: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Opcional - requerido para DGT"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País del Permiso
                </label>
                <input
                  type="text"
                  value={formData.conductor_pais_permiso}
                  onChange={(e) => setFormData({...formData, conductor_pais_permiso: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="ESP"
                />
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Esta identificación será manual. Se cobrará 6€ en lugar de 4€ (automática).
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/admin/multas/pendientes')}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {saving ? (
                  'Guardando...'
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Guardar y Notificar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default EditarMulta;
